import { FC } from 'react';
import { UserRole } from './ActionsPanel';
import { FiUsers, FiTrendingUp, FiTarget } from 'react-icons/fi';
import { useUser } from '../../context/UserContext';
import { canAccessRole } from '../../services/userService';

interface RoleSelectorProps {
  selectedRole: UserRole;
  onRoleChange: (role: UserRole) => void;
}

const RoleSelector: FC<RoleSelectorProps> = ({ selectedRole, onRoleChange }) => {
  // Get the active persona from context to determine accessible roles
  const { activePersona, allPersonas } = useUser();

  // Define role data with icons and descriptions
  const roles = [
    {
      id: 'CEO' as UserRole,
      icon: <FiTarget />,
      description: 'Strategic view',
      color: 'bg-brand-purple',
      textColor: 'text-brand-purple'
    },
    {
      id: 'VP of Lending' as UserRole,
      icon: <FiTrendingUp />,
      description: 'Portfolio view',
      color: 'bg-brand-link',
      textColor: 'text-brand-link'
    },
    {
      id: 'Loan Advisor' as UserRole,
      icon: <FiUsers />,
      description: 'Member view',
      color: 'bg-brand-blue',
      textColor: 'text-brand-blue'
    }
  ];

  // Filter roles based on what the current persona can access
  const accessibleRoles = roles.filter(role => 
    canAccessRole(activePersona.role, role.id)
  );

  // Get the avatar for each role from the allPersonas array
  const getAvatarForRole = (roleId: UserRole) => {
    const persona = allPersonas.find(p => p.role === roleId);
    return persona?.avatar || '';
  };

  return (
    <div className="flex items-center space-x-4 mt-4 md:mt-0">
      {accessibleRoles.map((role) => (
        <button
          key={role.id}
          onClick={() => onRoleChange(role.id)}
          className={`
            flex items-center px-4 py-2 rounded-md transition-all duration-200 w-[200px] justify-start
            ${selectedRole === role.id 
              ? `${role.color} text-white shadow-md` 
              : 'bg-white/10 text-white/70 hover:bg-white/20'
            }
          `}
        >
          <div className="w-8 h-8 rounded-full overflow-hidden mr-3 border border-white/20 flex-shrink-0">
            <img 
              src={getAvatarForRole(role.id)} 
              alt={`${role.id} avatar`}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="text-left flex-1">
            <div className="text-sm font-medium">{role.id}</div>
            <div className="text-xs opacity-80">{role.description}</div>
          </div>
        </button>
      ))}
    </div>
  );
};

export default RoleSelector; 